import React, { useEffect, useRef, useState, useMemo, memo, useCallback } from "react";
import { useChatAI } from "./chat-ai-context";
import { TextArea } from "@/components/base/textarea/textarea";
import { Button } from "@/components/base/buttons/button";
import { Paperclip, Recording01, ChevronDown, MagicWand01, ChevronLeft, ChevronRight, Stop } from "@untitledui/icons";
import { cx } from "@/utils/cx";
import { EveMindLogo } from "@/components/foundations/logo/eve-mind-logo";
import { ConversationalFormWizard } from "./conversational-form-wizard";
import { useTranslation } from "react-i18next";
import { getCurrentUserType } from "@/components/application/command-menu/command-menu-search";
import { useI18n } from "@/hooks/use-i18n";
import { ThinkingPanel } from "./thinking-panel";
import { MessageContent } from "./message-content";
import { MessageActions } from "./message-actions";
import { StopButton } from "./stop-button";
import { motion } from "motion/react";
import { useSpeechRecognition } from "./use-speech-recognition";
import { AttachmentPreview } from "./attachment-preview";
import { EmojiPicker } from "./emoji-picker";
import { useSetting } from "@/hooks/api/use-settings";
import { useDocumentMention } from "./hooks/use-document-mention";
import { DocumentMentionDropdown } from "./document-mention-dropdown";
import { useMe } from "@/hooks/api/use-auth";
import { CreateContainerIdModal } from "./create-container-id-modal";
import "./ai-chat-window.css";

// Componente memoizzato per i messaggi dell'assistente
const AssistantMessageItem = memo(({
    content,
    timestamp,
    avatar,
    uiResources
}: {
    content: string;
    timestamp?: number;
    avatar: React.ReactElement;
    uiResources?: Array<{
        uri: string;
        name?: string;
        mimeType: string;
        text?: string;
        blob?: string;
        _meta?: Record<string, any>;
        [key: string]: any;
    }>;
}) => {
    const { t } = useTranslation(['common']);

    const formatTimestamp = (ts?: number) => {
        if (!ts) return undefined;
        try {
            return new Date(ts).toLocaleString(undefined, {
                weekday: "long",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return undefined;
        }
    };

    return (
        <div className="flex items-start mb-1 group">
            {avatar}
            <div className="flex-1">
                <div className="mb-1 flex items-baseline pr-1 min-w-[180px] md:min-w-[220px] gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-primary">{t('aiChat.assistant')}</span>
                        {timestamp && <span className="text-xs text-tertiary">{formatTimestamp(timestamp)}</span>}
                    </div>
                    <div className="ml-auto">
                        <MessageActions
                            content={content}
                            role="assistant"
                        />
                    </div>
                </div>
                <div className="max-w-[800px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                    <MessageContent content={content} uiResources={uiResources} />
                </div>
            </div>
        </div>
    );
});

AssistantMessageItem.displayName = "AssistantMessageItem";

// Componente memoizzato per i messaggi dell'utente
const UserMessageItem = memo(({
    content,
    timestamp
}: {
    content: string;
    timestamp?: number;
}) => {
    const { t } = useTranslation(['common']);

    const formatTimestamp = (ts?: number) => {
        if (!ts) return undefined;
        try {
            return new Date(ts).toLocaleString(undefined, {
                weekday: "long",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return undefined;
        }
    };

    return (
        <div className="flex flex-col items-end group">
            <div className="mb-1 flex items-baseline pr-1 min-w-[180px] md:min-w-[220px] gap-3">
                <div className="ml-auto flex items-center gap-2">
                    {timestamp && <span className="text-xs text-tertiary">{formatTimestamp(timestamp)}</span>}
                    <span className="text-sm font-medium text-primary">{t('aiChat.you')}</span>
                </div>
                <div>
                    <MessageActions
                        content={content}
                        role="user"
                    />
                </div>
            </div>
            <div className="max-w-[800px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                <MessageContent content={content} />
            </div>
        </div>
    );
});

UserMessageItem.displayName = "UserMessageItem";

// Componente per il thinking come elemento separato nella chat
const ThinkingMessageItem = memo(({
    thinkingSteps,
    isStreaming
}: {
    thinkingSteps: Array<{
        id: string;
        step_type: "thinking" | "tool_call" | "rag_search" | "delegate" | "response" | "compaction";
        content: string;
        timestamp: string;
        agent_name: string;
        metadata?: Record<string, any>;
    }>;
    isStreaming: boolean;
}) => {
    // Non mostrare se non ci sono thinking steps e non stiamo streamando
    if (thinkingSteps.length === 0 && !isStreaming) {
        return null;
    }

    return (
        <div className="flex items-start mb-1 w-full">
            <div className="flex-1 w-full">
                <ThinkingPanel
                    thinkingSteps={thinkingSteps}
                    isStreaming={isStreaming}
                    defaultOpen={isStreaming}
                />
            </div>
        </div>
    );
});

ThinkingMessageItem.displayName = "ThinkingMessageItem";

export const AIChatWindow = () => {
    const { messages, submitPrompt, isTyping, isNewChat, activeWizard, isStreaming, stop, getThinkingStepsForMessage } = useChatAI();
    // messages è già di tipo ChatMessage[] dal context che include thinkingSteps e isStreaming
    const [text, setText] = useState("");
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [showQuickActionsManual, setShowQuickActionsManual] = useState(false);
    const [showCreateContainerModal, setShowCreateContainerModal] = useState(false);
    const [inputHeight, setInputHeight] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const inputFormRef = useRef<HTMLFormElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const editorRef = useRef<HTMLDivElement | null>(null);
    const quickActionsScrollRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation(['common']);
    const { currentLanguage } = useI18n();
    const userType = getCurrentUserType();
    const { data: currentUser } = useMe();

    // Fetch setting for enabling/disabling attachments
    // If user doesn't have permission to read settings, default to enabled (permissive)
    const { data: attachmentsSetting, error: attachmentsSettingError } = useSetting("eve_care_chat", "enable_attachments", "global");
    const attachmentsEnabled = attachmentsSettingError
        ? true // Default to enabled if permission denied or error
        : (attachmentsSetting?.value !== false); // Default to true if setting not loaded yet

    // Document mention system
    const {
        isActive: isMentionActive,
        query: mentionQuery,
        insertDocument,
        removeDocument,
        clearMention,
    } = useDocumentMention(editorRef);

    // Document preview state

    // Handle placeholder for contentEditable
    const [isEmpty, setIsEmpty] = useState(true);
    useEffect(() => {
        if (!editorRef.current) return;

        const checkEmpty = () => {
            const text = editorRef.current?.textContent?.trim() || "";
            const hasTags = editorRef.current?.querySelector(".inline-document-tag");
            setIsEmpty(text === "" && !hasTags);
        };

        const editor = editorRef.current;
        editor.addEventListener("input", checkEmpty);
        checkEmpty();

        return () => editor.removeEventListener("input", checkEmpty);
    }, []);

    // Event delegation for tag buttons and hover
    useEffect(() => {
        if (!editorRef.current) return;

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const removeBtn = target.closest(".tag-remove");

            if (removeBtn) {
                e.preventDefault();
                const docId = removeBtn.getAttribute("data-document-id");
                if (docId) removeDocument(docId);
            }
        };

        const editor = editorRef.current;
        editor.addEventListener("click", handleClick);

        return () => {
            editor.removeEventListener("click", handleClick);
        };
    }, [removeDocument]);

    // Handle paste events (only text)
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const textNode = document.createTextNode(text);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            if (editorRef.current) {
                editorRef.current.dispatchEvent(new Event("input", { bubbles: true }));
            }
        }
    }, []);

    // Map language code to Speech Recognition locale
    const getSpeechRecognitionLocale = (lang: string): string => {
        const localeMap: Record<string, string> = {
            "en": "en-US",
            "it": "it-IT",
            "es": "es-ES",
        };
        return localeMap[lang] || "en-US";
    };

    // Speech recognition
    const {
        isListening,
        startListening,
        stopListening,
        isSupported: isSpeechSupported,
    } = useSpeechRecognition({
        onTranscript: (transcript) => {
            // Aggiorna il testo - il controllo se l'ascolto è attivo è già fatto nel hook
            // Se questo callback viene chiamato, significa che l'ascolto è attivo
            setText(transcript);
        },
        language: getSpeechRecognitionLocale(currentLanguage),
    });

    // Gestisci toggle speech recognition
    const handleSpeechToggle = () => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    };

    // Avatar usando EveMindLogo (solo icona, senza testo)
    const AssistantAvatar = useMemo(() => {
        return (
            <div className="mr-3 flex-shrink-0">
                <EveMindLogo showText={false} className="h-8 w-8" />
            </div>
        );
    }, []);

    // Misura l'altezza dell'input form
    useEffect(() => {
        const form = inputFormRef.current;
        if (!form) return;

        const updateInputHeight = () => {
            const height = form.offsetHeight;
            setInputHeight(height);
        };

        // Misura iniziale
        updateInputHeight();

        // Usa ResizeObserver per tracciare i cambiamenti di dimensione
        const resizeObserver = new ResizeObserver(updateInputHeight);
        resizeObserver.observe(form);

        return () => resizeObserver.disconnect();
    }, []);

    // Scroll detection e auto-scroll
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const checkScrollPosition = () => {
            const threshold = 100; // threshold per considerare "al fondo"
            const isBottom =
                container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
            setIsAtBottom(isBottom);
        };

        // Check iniziale
        checkScrollPosition();

        container.addEventListener('scroll', checkScrollPosition);
        return () => container.removeEventListener('scroll', checkScrollPosition);
    }, []);

    // Calcola tutti i thinking steps per tracciare i cambiamenti
    const allThinkingSteps = useMemo(() => {
        const allSteps: Array<{ messageId: string; steps: ReturnType<typeof getThinkingStepsForMessage> }> = [];
        messages.forEach(m => {
            if (m.role === "user") {
                const steps = getThinkingStepsForMessage(m.id);
                allSteps.push({ messageId: m.id, steps });
            }
        });
        return allSteps;
    }, [messages, getThinkingStepsForMessage]);

    // Auto-scroll quando ci sono nuovi messaggi, thinking steps o durante streaming
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        // Usa un timeout per assicurarsi che il DOM sia aggiornato
        const timeoutId = setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            const currentScrollTop = container.scrollTop;
            const clientHeight = container.clientHeight;
            const isNearBottom = currentScrollTop + clientHeight >= newScrollHeight - 100;

            // Scroll sempre se stiamo streamando o se siamo già vicini al fondo
            if (isStreaming || isNearBottom || messages.length === 0) {
                container.scrollTo({ top: newScrollHeight, behavior: "smooth" });
            }
        }, 50); // Piccolo delay per permettere al DOM di aggiornarsi

        return () => clearTimeout(timeoutId);
    }, [messages.length, messages, isStreaming, allThinkingSteps]);

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
    };

    // File handling
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_FILES = 5; // Maximum number of files per message
    const ALLOWED_FILE_TYPES = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
        "text/csv",
    ];

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        // Check total file count limit
        const currentFileCount = attachedFiles.length;
        if (currentFileCount >= MAX_FILES) {
            console.warn(`Maximum ${MAX_FILES} files allowed per message`);
            return;
        }

        const newFiles: File[] = [];
        Array.from(files).forEach((file) => {
            // Check if we've reached the limit
            if (currentFileCount + newFiles.length >= MAX_FILES) {
                console.warn(`Maximum ${MAX_FILES} files allowed per message`);
                return;
            }

            // Validazione dimensione
            if (file.size > MAX_FILE_SIZE) {
                // TODO: Mostrare toast/alert per file troppo grande
                console.warn(`File ${file.name} is too large. Max size: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
                return;
            }

            // Validazione tipo file
            if (!ALLOWED_FILE_TYPES.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|gif|webp|doc|docx|xls|xlsx|txt|csv)$/i)) {
                // TODO: Mostrare toast/alert per tipo non supportato
                console.warn(`File type ${file.type} is not supported`);
                return;
            }

            newFiles.push(file);
        });

        setAttachedFiles((prev) => [...prev, ...newFiles]);
        // Reset input per permettere di selezionare lo stesso file di nuovo
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveFile = (index: number) => {
        setAttachedFiles((prev) => {
            const newFiles = [...prev];
            const removedFile = newFiles[index];
            // Cleanup preview URL se è un'immagine
            if (removedFile.type.startsWith("image/")) {
                // URL sarà revocato quando il componente AttachmentPreview si smonta
            }
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const handleEmojiSelect = (emoji: string) => {
        const textarea = textAreaRef.current;
        if (!textarea) {
            // Fallback: aggiungi alla fine del testo
            setText((prev) => prev + emoji);
            return;
        }

        // Inserisci emoji alla posizione del cursore
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = text;
        const newText = currentText.slice(0, start) + emoji + currentText.slice(end);

        setText(newText);

        // Riposiziona il cursore dopo l'emoji
        setTimeout(() => {
            if (textarea) {
                const newPosition = start + emoji.length;
                textarea.setSelectionRange(newPosition, newPosition);
                textarea.focus();
            }
        }, 0);
    };

    // Check scroll position for quick actions
    const checkQuickActionsScroll = useCallback(() => {
        const container = quickActionsScrollRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding errors
    }, []);

    // Scroll quick actions
    const scrollQuickActions = (direction: "left" | "right") => {
        const container = quickActionsScrollRef.current;
        if (!container) return;

        const scrollAmount = 200; // Scroll di 200px per volta
        const newScrollLeft = direction === "left"
            ? container.scrollLeft - scrollAmount
            : container.scrollLeft + scrollAmount;

        container.scrollTo({
            left: newScrollLeft,
            behavior: "smooth",
        });
    };

    // Monitor scroll position changes
    useEffect(() => {
        // Check if quick actions should be shown
        const shouldShow = (isNewChat && messages.length === 0) || showQuickActionsManual;
        if (!shouldShow) return;

        const container = quickActionsScrollRef.current;
        if (!container) return;

        // Check after a small delay to ensure DOM is updated
        const timeoutId = setTimeout(() => {
            checkQuickActionsScroll();
        }, 100);

        container.addEventListener("scroll", checkQuickActionsScroll);
        // Check on resize
        const resizeObserver = new ResizeObserver(checkQuickActionsScroll);
        resizeObserver.observe(container);

        return () => {
            clearTimeout(timeoutId);
            container.removeEventListener("scroll", checkQuickActionsScroll);
            resizeObserver.disconnect();
        };
    }, [checkQuickActionsScroll, isNewChat, messages.length, showQuickActionsManual]);

    // Extract plain text from contentEditable (excluding tags)
    const getPlainText = useCallback((): string => {
        if (!editorRef.current) return text; // Fallback to text state

        let plainText = "";
        const walker = document.createTreeWalker(
            editorRef.current,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            {
                acceptNode: (node) => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const el = node as HTMLElement;
                        if (el.classList.contains("inline-document-tag")) {
                            return NodeFilter.FILTER_REJECT;
                        }
                    }
                    return NodeFilter.FILTER_SKIP;
                },
            }
        );

        while (walker.nextNode()) {
            plainText += walker.currentNode.textContent;
        }

        return plainText.trim();
    }, [text]);

    // Extract document IDs from contentEditable tags
    const extractDocumentIds = useCallback((): string[] => {
        if (!editorRef.current) return [];

        const tags = editorRef.current.querySelectorAll(".inline-document-tag");
        return Array.from(tags)
            .map((tag) => (tag as HTMLElement).dataset.documentId)
            .filter((id): id is string => !!id);
    }, []);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isStreaming) return;

        // Get plain text and document IDs
        const plainText = editorRef.current ? getPlainText() : text.trim();
        const documentIds = extractDocumentIds();

        if (!plainText && attachedFiles.length === 0 && documentIds.length === 0) return;

        // Use submitPrompt which will trigger the streaming
        // Pass files and document IDs if any are attached/mentioned
        if (submitPrompt) {
            const filesToSend = attachedFiles.length > 0 ? attachedFiles : undefined;
            const documentIdsToSend = documentIds.length > 0 ? documentIds : undefined;
            submitPrompt(plainText || "", filesToSend, documentIdsToSend);
        }

        // Clear input and files after submission
        if (editorRef.current) {
            editorRef.current.innerHTML = "";
        }
        setText("");
        setAttachedFiles([]);
        clearMention();
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        // Se dropdown aperto, non gestire Enter (gestito da DocumentMentionDropdown)
        if (isMentionActive) return;

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit(e);
        }
    };

    type QuickActionId = "createContainer" | "containerRiskAssessment" | "missingDocs" | "containerUpdates";

    const handleQuickActionClick = (action: { label: string; actionId: QuickActionId }) => {
        if (action.actionId === "createContainer") {
            setShowCreateContainerModal(true);
        } else {
            const prompt = t(`aiChat.quickActionPrompts.${action.actionId}`);
            submitPrompt(prompt);
        }
    };

    const handleCreateContainerSubmit = (containerId: string) => {
        const prompt = t("aiChat.quickActionPrompts.createContainer", { containerId });
        submitPrompt(prompt);
        setShowCreateContainerModal(false);
    };

    const getQuickActions = (): Array<{ label: string; actionId: QuickActionId }> => {
        if (!userType) return [];

        return [
            { label: t("aiChat.actions.createContainer"), actionId: "createContainer" },
            { label: t("aiChat.actions.containerRiskAssessment"), actionId: "containerRiskAssessment" },
            { label: t("aiChat.actions.missingDocs"), actionId: "missingDocs" },
            { label: t("aiChat.actions.containerUpdates"), actionId: "containerUpdates" },
        ];
    };

    // Mostra wizard se attivo
    if (activeWizard) {
        return <ConversationalFormWizard />;
    }

    // Determina se mostrare quick actions
    const shouldShowQuickActions = (isNewChat && messages.length === 0) || showQuickActionsManual;

    return (
        <div className="flex h-full min-h-0 w-full overflow-hidden">
            <CreateContainerIdModal
                isOpen={showCreateContainerModal}
                onClose={() => setShowCreateContainerModal(false)}
                onSubmit={handleCreateContainerSubmit}
            />
            {/* Colonna principale: Messaggi e Input */}
            <div className="flex flex-1 flex-col min-w-0 relative h-full overflow-hidden">
                {/* Area messaggi scrollabile */}
                <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto relative"
                >
                    {/* Contenuto messaggi */}
                    <div className="p-4 md:p-6 pb-32">
                        <div className="flex flex-col gap-6 w-full mx-auto" ref={listRef}>
                            {/* Mostra messaggio iniziale dell'assistente se nuova chat e nessun wizard attivo */}
                            {isNewChat && !activeWizard && messages.length === 0 && (
                                <AssistantMessageItem
                                    content={t('aiChat.initialMessage')}
                                    timestamp={Date.now()}
                                    avatar={AssistantAvatar}
                                />
                            )}

                            {/* Mostra messaggi se presenti */}
                            {messages.map((m, index) => {
                                const isLastMessage = index === messages.length - 1;
                                const messageElements: React.ReactNode[] = [];

                                // Render message
                                messageElements.push(
                                    <motion.div
                                        key={m.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: index * 0.05 }}
                                        className={cx(m.role === "user" ? "self-end" : "self-start")}
                                    >
                                        {m.role === "assistant" && (
                                            <div className="max-w-4xl">
                                                <AssistantMessageItem
                                                    content={m.content}
                                                    timestamp={m.timestamp}
                                                    avatar={AssistantAvatar}
                                                    uiResources={m.uiResources}
                                                />
                                            </div>
                                        )}
                                        {m.role === "user" && (
                                            <div className="max-w-4xl">
                                                <UserMessageItem
                                                    content={m.content}
                                                    timestamp={m.timestamp}
                                                />
                                            </div>
                                        )}
                                    </motion.div>
                                );

                                // If this is a user message, check if there are thinking steps for it
                                if (m.role === "user") {
                                    const stepsForMessage = getThinkingStepsForMessage(m.id);
                                    // Check if we're streaming and this is the last message (waiting for assistant response)
                                    // OR if there's a corresponding assistant message that's still streaming
                                    const nextAssistantMessage = messages[index + 1];
                                    const isLastUserMessage = isLastMessage && m.role === "user";
                                    const isThinkingStreaming = Boolean(isStreaming && (
                                        isLastUserMessage ||
                                        (nextAssistantMessage?.role === "assistant" && nextAssistantMessage?.isStreaming)
                                    ));

                                    if (stepsForMessage.length > 0 || isThinkingStreaming) {
                                        messageElements.push(
                                            <motion.div
                                                key={`thinking-${m.id}`}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: (index + 1) * 0.05 }}
                                                className="self-start w-full"
                                            >
                                                <ThinkingMessageItem
                                                    thinkingSteps={stepsForMessage}
                                                    isStreaming={isThinkingStreaming}
                                                />
                                            </motion.div>
                                        );
                                    }
                                }

                                return messageElements;
                            }).flat()}

                            {isTyping && (
                                <div className="flex items-center">
                                    {AssistantAvatar}
                                    <div className="inline-flex items-center gap-2">
                                        <span className="text-sm font-medium text-primary">{t('aiChat.assistant')}</span>
                                        <span className="rounded-2xl border border-secondary bg-primary px-2 py-1">
                                            <span className="inline-flex items-center gap-1">
                                                <span className="size-1.5 rounded-full bg-gray-500 animate-pulse [animation-delay:-0.3s]" />
                                                <span className="size-1.5 rounded-full bg-gray-400 animate-pulse [animation-delay:-0.15s]" />
                                                <span className="size-1.5 rounded-full bg-gray-300 animate-pulse" />
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Spacer per l'altezza dell'input */}
                            {inputHeight > 0 && (
                                <div
                                    style={{ height: `${inputHeight}px` }}
                                    className="w-full flex-shrink-0"
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    </div>

                    {/* Pulsante scroll to bottom */}
                    {!isAtBottom && (
                        <div className="sticky bottom-20 z-20 flex justify-center mb-2">
                            <button
                                onClick={scrollToBottom}
                                className="rounded-full bg-primary border border-secondary shadow-lg px-4 py-2 flex items-center gap-2 text-sm text-primary hover:bg-secondary transition-colors"
                                aria-label={t('aiChat.scrollToBottom', { defaultValue: "Scroll to bottom" })}
                            >
                                <ChevronDown className="size-4" />
                                <span>{t('aiChat.scrollToBottom', { defaultValue: "Scroll to bottom" })}</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Quick Actions - sopra l'input field */}
                {shouldShowQuickActions && getQuickActions().length > 0 && (
                    <div className="px-4 md:px-6 py-2 flex items-center gap-2">
                        {/* Left scroll button */}
                        {canScrollLeft && (
                            <button
                                type="button"
                                onClick={() => scrollQuickActions("left")}
                                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary border border-secondary shadow-lg hover:bg-secondary transition-colors cursor-pointer"
                                aria-label={t('aiChat.scrollLeft', { defaultValue: "Scroll left" })}
                            >
                                <ChevronLeft className="size-4 text-primary" />
                            </button>
                        )}

                        {/* Scrollable container */}
                        <div
                            ref={quickActionsScrollRef}
                            className="flex-1 flex gap-2 overflow-x-auto scrollbar-hide scroll-smooth"
                            style={{
                                scrollbarWidth: "none",
                                msOverflowStyle: "none",
                            }}
                        >
                            <style>{`
                                .scrollbar-hide::-webkit-scrollbar {
                                    display: none;
                                }
                            `}</style>
                            {getQuickActions().map((action) => (
                                <Button
                                    key={action.actionId}
                                    onClick={() => handleQuickActionClick(action)}
                                    color="secondary"
                                    size="sm"
                                    className="text-sm flex-shrink-0"
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>

                        {/* Right scroll button */}
                        {canScrollRight && (
                            <button
                                type="button"
                                onClick={() => scrollQuickActions("right")}
                                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary border border-secondary shadow-lg hover:bg-secondary transition-colors cursor-pointer"
                                aria-label={t('aiChat.scrollRight', { defaultValue: "Scroll right" })}
                            >
                                <ChevronRight className="size-4 text-primary" />
                            </button>
                        )}
                    </div>
                )}

                {/* Attachment Preview */}
                {attachedFiles.length > 0 && (
                    <div className="sticky bottom-0 bg-transparent">
                        <AttachmentPreview files={attachedFiles} onRemove={handleRemoveFile} />
                    </div>
                )}

                {/* Input area */}
                <div className="sticky bottom-0 border-t border-secondary bg-primary">
                    <form ref={inputFormRef} onSubmit={onSubmit} className="py-3 md:py-4 px-4 md:px-6">
                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={!attachmentsEnabled}
                        aria-label={t('aiChat.selectFiles', { defaultValue: "Select files" })}
                    />
                    <div
                        className="relative w-full rounded-lg bg-transparent ring-1 ring-primary transition-shadow duration-100 ease-linear focus-within:ring-2 focus-within:ring-brand"
                    >
                        {/* Top-right speech recognition button */}
                        {isSpeechSupported && (
                            <button
                                type="button"
                                onClick={handleSpeechToggle}
                                className={cx(
                                    "pointer-events-auto absolute right-3 top-3 flex items-center justify-center rounded-md p-1.5 transition-colors cursor-pointer",
                                    isListening
                                        ? "bg-error-primary text-white animate-pulse"
                                        : "text-tertiary hover:text-tertiary_hover hover:bg-secondary"
                                )}
                                aria-label={isListening ? t('aiChat.stopRecording', { defaultValue: "Stop recording" }) : t('aiChat.startRecording', { defaultValue: "Start recording" })}
                                title={isListening ? t('aiChat.stopRecording', { defaultValue: "Stop recording" }) : t('aiChat.startRecording', { defaultValue: "Start recording" })}
                            >
                                {isListening ? (
                                    <Stop className="size-4" />
                                ) : (
                                    <Recording01 className="size-4" />
                                )}
                            </button>
                        )}

                        {/* ContentEditable for document mentions */}
                        <div className="relative">
                            <div
                                ref={editorRef}
                                contentEditable
                                onKeyDown={onKeyDown}
                                onPaste={handlePaste}
                                data-placeholder={t('aiChat.placeholder', { defaultValue: "Ask Eve... (usa @ per menzionare documenti)" })}
                                className={cx(
                                    "min-h-[88px] max-h-[200px] overflow-y-auto resize-none border-0 bg-transparent px-3.5 pb-10 pt-3 shadow-none ring-0 outline-hidden",
                                    "text-primary",
                                    "focus:outline-none",
                                    isEmpty && "before:content-[attr(data-placeholder)] before:text-tertiary before:pointer-events-none before:absolute before:left-3.5 before:top-3"
                                )}
                                style={{ color: 'var(--color-text-primary)' }}
                                suppressContentEditableWarning
                            />
                        </div>

                        {/* Keep TextArea as fallback for speech recognition */}
                        <TextArea
                            value={text}
                            onChange={(val) => setText(val as string)}
                            rows={4}
                            placeholder=""
                            textAreaRef={textAreaRef}
                            textAreaClassName="hidden"
                            onKeyDown={onKeyDown}
                            className="hidden"
                        />

                        {/* Bottom-right actions */}
                        <div className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-2">
                            {/* Toggle Quick Actions - sempre visibile usando Button di Untitled UI */}
                            <Button
                                type="button"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.preventDefault();
                                    setShowQuickActionsManual(!showQuickActionsManual);
                                }}
                                color={shouldShowQuickActions ? "secondary" : "tertiary"}
                                size="sm"
                                iconLeading={MagicWand01}
                                className="pointer-events-auto"
                                aria-label={t('aiChat.toggleQuickActions', { defaultValue: "Toggle quick actions" })}
                                title={t('aiChat.toggleQuickActions', { defaultValue: "Toggle quick actions" })}
                            />
                            {attachmentsEnabled && (
                                <button
                                    type="button"
                                    onClick={handleAttachClick}
                                    className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover cursor-pointer"
                                    aria-label={t('aiChat.attach', { defaultValue: "Attach file" })}
                                    title={t('aiChat.attach', { defaultValue: "Attach file" })}
                                >
                                    <Paperclip className="size-4" />
                                </button>
                            )}
                            <EmojiPicker
                                onEmojiSelect={handleEmojiSelect}
                                trigger={
                                    <button
                                        type="button"
                                        className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover cursor-pointer"
                                        aria-label={t('aiChat.emoji', { defaultValue: "Insert emoji" })}
                                        title={t('aiChat.emoji', { defaultValue: "Insert emoji" })}
                                    >
                                        <span className="block text-base leading-none">☺︎</span>
                                    </button>
                                }
                            />
                            {isStreaming ? (
                                <span className="pointer-events-auto">
                                    <StopButton
                                        variant="compact"
                                        onStop={stop || (() => {})}
                                        isStreaming={isStreaming}
                                    />
                                </span>
                            ) : (
                                <Button className="pointer-events-auto" color="link-color" size="md" type="submit">
                                    {t('buttons.send')}
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
                </div>

                {/* Document Mention Dropdown */}
                {currentUser?.company_id && (
                    <DocumentMentionDropdown
                        query={mentionQuery}
                        isOpen={isMentionActive}
                        anchorEl={editorRef.current}
                        companyId={currentUser.company_id}
                        onSelect={(doc) => {
                            insertDocument(doc.file_name || "Untitled Document", doc.document_id);
                        }}
                        onClose={clearMention}
                    />
                )}

            </div>
        </div>
    );
};


